import clsx from 'clsx';
import {
  forwardRef,
  type PropsWithChildren,
  type ReactElement,
  useImperativeHandle,
  useRef,
} from 'react';

import { HoneycombChartLayout } from './components/HoneycombChartLayout.js';
import {
  DEFAULT_HONEYCOMB_HEIGHT,
  HONEYCOMB_CHART_ARIA_LABEL,
} from './constants.js';
import { HoneycombColorSchemeContext } from './context/honeycomb-color-scheme.context.js';
import { HoneycombFormatterContext } from './context/honeycomb-formatter.context.js';
import { HoneycombLoadingContext } from './context/honeycomb-loading.context.js';
import { HoneycombShapeContext } from './context/honeycomb-shape.context.js';
import { HoneycombShowLabelsContext } from './context/honeycomb-show-labels.context.js';
import { HoneycombTruncationMode } from './context/honeycomb-truncation-mode.context.js';
import { useHoneycombChartConfig } from './hooks/use-honeycomb-chart-config.js';
import { HoneycombBaseScaleProvider } from './providers/honeycomb-base-scale.provider.js';
import { HoneycombConfigurationProvider } from './providers/honeycomb-configuration.provider.js';
import { HoneycombDatasetTypeProvider } from './providers/honeycomb-dataset-type.provider.js';
import { HoneycombFilteredDataProvider } from './providers/honeycomb-filtered-data.provider.js';
import { HoneycombMetadataProvider } from './providers/honeycomb-metadata.provider.js';
import { HoneycombStoreProvider } from './providers/honeycomb-store.provider.js';
import { HoneycombLegend } from './slots/HoneycombLegend.js';
import { HoneycombChartProps, HoneycombChartRef } from './types/honeycomb.js';
import { buildAndDownloadCsv } from './utils/build-and-download-csv.js';
import { normalizeTilesData } from './utils/normalize-tiles-data.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { Intent } from '../../core/slots/Intent/Intent.js';
import { defaultChartSize } from '../../core/styles/defaultChartValues.sty.js';
import { ChartSeriesActionContext } from '../core/components/series-action/ChartSeriesActionContext.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';
import { ChartType } from '../core/types/chart-types.js';
import { coerceSizeValue } from '../core/utils/coerceSizeValue.js';

/** The honeycomb component */
export const _HoneycombChart = forwardRef<
  HoneycombChartRef,
  PropsWithChildren<HoneycombChartProps>
>((props, forwardedRef) => {
  const {
    data,
    seriesActions,
    height,
    style: costumerStyle,
    className: customerClassNames,
    loading,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const chartHeight = coerceSizeValue(height) || DEFAULT_HONEYCOMB_HEIGHT;
  const rootRef = useMergeRefs<HoneycombChartRef | HTMLDivElement>([
    forwardedRef,
    containerRef,
  ]);

  const sanitizedData = normalizeTilesData(data);

  const config = useHoneycombChartConfig(props);

  const formatterContext = {
    formatter: config.formatter,
    unit: config.unit,
  };

  useImperativeHandle(forwardedRef, () => ({
    element: containerRef.current,
    downloadData: () => {
      buildAndDownloadCsv(sanitizedData);
    },
    getConfig: () => {
      return JSON.stringify({
        type: ChartType.Honeycomb,
        ...config,
      });
    },
  }));

  return (
    <div
      ref={rootRef}
      style={{ ...costumerStyle, height: chartHeight }}
      className={clsx(defaultChartSize, customerClassNames)}
      data-testid="honeycomb-container"
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      aria-label={HONEYCOMB_CHART_ARIA_LABEL}
      role="img"
    >
      <HoneycombLoadingContext.Provider value={loading}>
        <HoneycombConfigurationProvider config={config}>
          <HoneycombShapeContext.Provider value={config.shape}>
            <HoneycombColorSchemeContext.Provider value={config.colorScheme}>
              <HoneycombTruncationMode.Provider value={config.truncationMode}>
                <HoneycombFormatterContext.Provider value={formatterContext}>
                  <HoneycombShowLabelsContext.Provider
                    value={config.showLabels}
                  >
                    <HoneycombFilteredDataProvider
                      data={sanitizedData}
                      config={config}
                    >
                      <HoneycombDatasetTypeProvider>
                        <HoneycombBaseScaleProvider>
                          <ChartSeriesActionContext.Provider
                            value={{
                              // We need a cast here as the provider expects unknown and the
                              // react context will not be able to carry the generic anyways.
                              chartSeriesActions: seriesActions as (
                                series: unknown,
                              ) => ReactElement<PropsWithChildren>,
                            }}
                          >
                            <HoneycombStoreProvider>
                              <HoneycombMetadataProvider>
                                <HoneycombChartLayout {...props} />
                              </HoneycombMetadataProvider>
                            </HoneycombStoreProvider>
                          </ChartSeriesActionContext.Provider>
                        </HoneycombBaseScaleProvider>
                      </HoneycombDatasetTypeProvider>
                    </HoneycombFilteredDataProvider>
                  </HoneycombShowLabelsContext.Provider>
                </HoneycombFormatterContext.Provider>
              </HoneycombTruncationMode.Provider>
            </HoneycombColorSchemeContext.Provider>
          </HoneycombShapeContext.Provider>
        </HoneycombConfigurationProvider>
      </HoneycombLoadingContext.Provider>
    </div>
  );
});

(
  _HoneycombChart as typeof _HoneycombChart & { displayName: string }
).displayName = 'HoneycombChart';

/**
 * The `HoneycombChart` is used to display data in a grid using various shapes
 * for data points.
 * @public
 */
export const HoneycombChart = Object.assign(_HoneycombChart, {
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
  Legend: HoneycombLegend,
  Intent,
});
